import { I18NServiceV1 } from '@oneaudi/i18n-service';
import type { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
import { useCallback } from 'react';
import { i18nKeys } from './i18nKeys';

import { windowAvailable } from '~/utils/client-detection';
import { useAppState } from '../contexts/app-context';

export { i18nKeys } from './i18nKeys';

export const i18nScope = ['ngc', 'items'];

export interface UseI18nArgs {
	id: string;
	defaultMessage: string;
}

export type Translations = Record<string, string>;
export type Placeholder = Record<string, number | string | undefined | null>;

export type UseI18n = {
	(message: keyof typeof i18nKeys, placeholder?: Record<string, number | string | undefined | null>): string;
};

export function useI18n() {
	const textKeyMode = windowAvailable()
		? new URLSearchParams(window.location.search).get('d_text') === 'true'
		: undefined;
	const { translations } = useAppState();

	const keyExists = (key: keyof typeof i18nKeys): boolean => {
		return !!Object.keys(i18nKeys).find((i18nKey) => key === i18nKey);
	};

	const i18n: UseI18n = useCallback(
		(key, placeholder) => {
			if (!keyExists(key)) {
				return `missing translation for key: ${key}`;
			}

			const defaultMessage = i18nKeys[key];
			const translation = translations[key];

			if (textKeyMode) {
				return `[${key}]${translation ?? '-'}[END]`;
			}

			if (placeholder) {
				return Object.entries(placeholder).reduce(
					(replacedMessage, [currentPlaceholder, currentValue]) =>
						replacedMessage?.replace(`{{${currentPlaceholder}}}`, `${currentValue}`),
					translation ?? defaultMessage,
				);
			}

			return translation ?? defaultMessage ?? `missing translation: ${key}`;
		},
		[translations, textKeyMode], // Dependencies that could change
	);

	return { i18n, i18nKeys };
}

export async function fetchTranslations(
	localeService: GfaLocaleServiceV1,
	i18nService: I18NServiceV1,
): Promise<Translations> {
	return new Promise((resolve, reject) => {
		try {
			i18nService.setScopes(i18nScope);
			i18nService.registerCallback((messages) => {
				const res = Object.entries(messages[localeService.language]).reduce(
					(acc: Translations, [key, value]) => {
						i18nScope.forEach((scope) => {
							// Filter i18n keys relevant for the given scope.
							if (key.includes(scope)) {
								acc[key] = value;
							}
						});

						return acc;
					},
					{},
				);

				resolve(res);
			});
		} catch (err) {
			reject(err);
		}
	});
}
